import React, { useEffect, useLayoutEffect, useRef } from "react";
import { Stack } from "@mui/material";
import {
  TRACKED_CALIBRATIONS_STORAGE_KEY,
  TrackedCalibration,
  useTrackedCalibrations,
} from "./tracked-calibrations";
import { CalibrationBannerAlert } from "./CalibrationBannerAlert";
import { useCalibrationPolling } from "./useCalibrationPolling";
import { useAuth } from "../../API/auth";

function CalibrationBanner({
  calibration,
}: {
  calibration: TrackedCalibration;
}) {
  const hide = useTrackedCalibrations((state) => state.hide);
  const dismiss = useTrackedCalibrations((state) => state.dismiss);
  useCalibrationPolling(calibration);

  const { status, customerId, fieldId, sourceResultSetId, resultSetId } =
    calibration;
  if (calibration.dismissed) return null;
  if (status === "in_progress" && calibration.hidden) return null;

  const resultSetPath = `/customer/${customerId}/field/${fieldId}/result-set`;
  const onDismiss = () => dismiss(sourceResultSetId);

  return (
    <CalibrationBannerAlert
      status={status}
      fieldName={calibration.fieldName}
      errorCode={calibration.errorCode}
      calibrationLink={`${resultSetPath}/${resultSetId}/calibrate`}
      onClose={
        status === "in_progress" ? () => hide(sourceResultSetId) : onDismiss
      }
      onFollowLink={onDismiss}
    />
  );
}

const BANNER_HEIGHT_VAR = "--calibration-banner-height";

export function CalibrationBanners() {
  const username = useAuth((state) => state.username);
  const allCalibrations = useTrackedCalibrations((state) => state.calibrations);
  const calibrations = allCalibrations.filter(
    (calibration) => calibration.username === username,
  );
  const pruneExpired = useTrackedCalibrations((state) => state.pruneExpired);
  const forgetOtherUsers = useTrackedCalibrations(
    (state) => state.forgetOtherUsers,
  );

  useEffect(() => {
    pruneExpired();
  }, [pruneExpired]);

  useEffect(() => {
    if (username) forgetOtherUsers(username);
  }, [username, forgetOtherUsers]);

  const stackRef = useRef<HTMLDivElement>(null);
  const hasBanners = calibrations.length > 0;

  useLayoutEffect(() => {
    const root = document.documentElement.style;
    const stack = stackRef.current;
    const publishHeight = () =>
      root.setProperty(BANNER_HEIGHT_VAR, `${stack?.offsetHeight ?? 0}px`);
    publishHeight();
    if (!stack || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(publishHeight);
    observer.observe(stack);
    return () => {
      observer.disconnect();
      root.setProperty(BANNER_HEIGHT_VAR, "0px");
    };
  }, [hasBanners]);

  useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      if (event.key === TRACKED_CALIBRATIONS_STORAGE_KEY) {
        void useTrackedCalibrations.persist.rehydrate();
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  if (!hasBanners) return null;

  return (
    <Stack ref={stackRef} sx={{ "& .MuiAlert-root": { borderRadius: 0 } }}>
      {calibrations.map((calibration) => (
        <CalibrationBanner
          key={calibration.sourceResultSetId}
          calibration={calibration}
        />
      ))}
    </Stack>
  );
}
